import { isTabAllowed } from '@revelio/auth';
import { Tab } from '@revelio/data-access';

import type { ChartType } from '../../PageConfigProvider/types/chart';
import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import {
  type GroupedMetricOption,
  isTalentDiscoveryMetric,
  type MetricValues,
} from '../../PageConfigProvider/types/metric';
import {
  routesToCompensation,
  routesToComposition,
  routesToPosting,
  routesToScreener,
  routesToSentiment,
  routesToTransition,
} from './data-provider-routing';

const DATA_SOURCE_ACCESS_MESSAGE = (dataSourceLabel: string) =>
  `This chart uses ${dataSourceLabel} data, which is not available for your account. Please contact your Revelio representative.`;

type ChartDataSourceAccessArgs = {
  metric: MetricValues | undefined;
  chartType: ChartType | undefined;
  secondaryDimension?: SecondaryDimensionValues;
  userTabs?: Tab[];
};

type DataSourceAccessRequirement = {
  tabs: Tab[];
  label: string;
};

const COMBINATION_METRIC_REQUIREMENTS: Partial<
  Record<MetricValues, DataSourceAccessRequirement>
> = {
  entity_summary: { tabs: [Tab.EntitySummary], label: 'Company Summary' },
  demand_supply: {
    tabs: [Tab.Overview, Tab.Posting],
    label: 'Composition and Job Postings',
  },
  demand_supply_percentile: {
    tabs: [Tab.Overview, Tab.Posting],
    label: 'Composition and Job Postings',
  },
  market_tightness: {
    tabs: [Tab.Overview, Tab.Posting],
    label: 'Composition and Job Postings',
  },
  market_share: {
    tabs: [Tab.Overview, Tab.Posting, Tab.Sentiment],
    label: 'Composition, Job Postings, and Sentiment',
  },
  target_companies_rec: {
    tabs: [Tab.Overview, Tab.Posting, Tab.Sentiment],
    label: 'Composition, Job Postings, and Sentiment',
  },
  wage_pressure: { tabs: [Tab.Overview], label: 'Compositions' },
  wage_pressure_percentile: { tabs: [Tab.Overview], label: 'Compositions' },
  overview_map: { tabs: [Tab.Overview], label: 'Compositions' },
  positive_review: { tabs: [Tab.Sentiment], label: 'Sentiment' },
  negative_review: { tabs: [Tab.Sentiment], label: 'Sentiment' },
};

function getChartDataSourceAccessRequirement({
  metric,
  chartType,
  secondaryDimension,
}: Omit<ChartDataSourceAccessArgs, 'userTabs'>):
  | DataSourceAccessRequirement
  | undefined {
  if (metric === undefined) {
    return undefined;
  }

  const combinationRequirement = COMBINATION_METRIC_REQUIREMENTS[metric];
  if (combinationRequirement) {
    return combinationRequirement;
  }

  if (isTalentDiscoveryMetric(metric)) {
    return { tabs: [Tab.TalentDiscovery], label: 'Talent Discovery' };
  }

  if (chartType === undefined) {
    return undefined;
  }

  if (routesToComposition(metric, chartType)) {
    return { tabs: [Tab.Overview], label: 'Compositions' };
  }

  if (routesToTransition(metric, chartType, secondaryDimension)) {
    return { tabs: [Tab.Transition], label: 'Transitions' };
  }

  if (routesToPosting(metric, chartType, secondaryDimension)) {
    return { tabs: [Tab.Posting], label: 'Job Postings' };
  }

  if (routesToSentiment(metric, chartType)) {
    return { tabs: [Tab.Sentiment], label: 'Sentiment' };
  }

  if (routesToCompensation(metric, chartType, secondaryDimension)) {
    return { tabs: [Tab.Compensation], label: 'Pay' };
  }

  if (routesToScreener(metric, chartType)) {
    return { tabs: [Tab.Screener], label: 'Screener' };
  }

  return undefined;
}

function isRequirementSatisfied(
  requirement: DataSourceAccessRequirement,
  userTabs: Tab[]
): boolean {
  return requirement.tabs.every((tab) => isTabAllowed(tab, userTabs));
}

export function canAccessMetricForChart({
  userTabs = [],
  ...args
}: ChartDataSourceAccessArgs): boolean {
  const requirement = getChartDataSourceAccessRequirement(args);
  return !requirement || isRequirementSatisfied(requirement, userTabs);
}

export function getChartDataSourceAccessErrorMessage({
  userTabs = [],
  ...args
}: ChartDataSourceAccessArgs): string | undefined {
  const requirement = getChartDataSourceAccessRequirement(args);

  if (requirement && !isRequirementSatisfied(requirement, userTabs)) {
    return DATA_SOURCE_ACCESS_MESSAGE(requirement.label);
  }

  return undefined;
}

export type AccessAnnotatedMetricOption =
  GroupedMetricOption['options'][number] & {
    isDisabled?: boolean;
  };

export type AccessAnnotatedMetricGroup = Omit<
  GroupedMetricOption,
  'options'
> & {
  options: AccessAnnotatedMetricOption[];
};

export function annotateMetricGroupsByAccess(
  groups: GroupedMetricOption[],
  {
    chartType,
    secondaryDimension,
    userTabs,
  }: {
    chartType: ChartType | undefined;
    secondaryDimension?: SecondaryDimensionValues;
    userTabs: Tab[];
  }
): AccessAnnotatedMetricGroup[] {
  return groups.map((group) => ({
    ...group,
    options: group.options.map((option) => {
      const isDisabled = !canAccessMetricForChart({
        metric: option.value,
        chartType,
        secondaryDimension,
        userTabs,
      });
      return isDisabled
        ? { ...option, label: `${option.label} (Unavailable)`, isDisabled }
        : option;
    }),
  }));
}
